import { createRouter, createWebHistory } from 'vue-router'
import type { RouteLocationRaw } from 'vue-router'

import { loadMe } from '@/libs/me'
import { useAppStore } from '@/stores/app'

const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) return savedPosition
        if (from.path === to.path) return {}

        return { top: 0 }
    },
    routes: [
        { path: '/', redirect: { name: 'login' } },
        {
            path: '/login',
            name: 'login',
            component: () => import('@/views/auth/Login.vue'),
            meta: { layout: 'Blank', requiresAuth: false },
        },
        {
            path: '/users',
            name: 'users',
            component: () => import('@/views/user/Users.vue'),
            meta: { requiresAuth: true, adminOnly: true },
        },
        {
            path: '/projects',
            name: 'projects',
            component: () => import('@/views/project/Projects.vue'),
            meta: { requiresAuth: true, adminOnly: true },
        },
        {
            path: '/devices',
            name: 'devices',
            component: () => import('@/views/device/Devices.vue'),
            meta: { requiresAuth: true },
        },
        {
            path: '/devices/:id',
            name: 'device-detail',
            component: () => import('@/views/device/Device.vue'),
            meta: { requiresAuth: true },
        },
        {
            path: '/devices/:id/config',
            name: 'device-config',
            component: () => import('@/views/device/DeviceConfig.vue'),
            meta: { requiresAuth: true },
        },
        {
            path: '/module-types',
            name: 'module-types',
            component: () => import('@/views/Placeholder.vue'),
            meta: { requiresAuth: true, adminOnly: true },
        },
        {
            path: '/managed-play',
            name: 'managed-play',
            component: () => import('@/views/android/ManagedPlay.vue'),
            meta: { requiresAuth: true, adminOnly: true },
        },
        {
            path: '/data',
            redirect: { name: 'audit-card-reads' },
        },
        {
            path: '/audit',
            children: [
                {
                    path: 'card-reads',
                    name: 'audit-card-reads',
                    component: () => import('@/views/audit/CardReads.vue'),
                    meta: { requiresAuth: true },
                },
                {
                    path: 'exchanges',
                    name: 'audit-exchanges',
                    component: () => import('@/views/audit/Exchanges.vue'),
                    meta: { requiresAuth: true },
                },
                {
                    path: 'events',
                    name: 'audit-events',
                    component: () => import('@/views/audit/Events.vue'),
                    meta: { requiresAuth: true },
                },
            ],
        },
    ],
})

function landing(isAdmin: boolean): RouteLocationRaw {
    return isAdmin ? { name: 'users' } : { name: 'devices' }
}

router.beforeEach(async (to) => {
    const appStore = useAppStore()

    if (appStore.isLoggedIn() && !appStore.userData) {
        const ok = await loadMe()
        if (!ok) {
            appStore.setLogout()

            return to.name === 'login' ? true : { name: 'login' }
        }
    }

    const loggedIn = appStore.isLoggedIn()

    if (to.name === 'login') {
        return loggedIn ? landing(appStore.isAdmin()) : true
    }

    if (to.meta.requiresAuth && !loggedIn) {
        return { name: 'login' }
    }

    if (to.meta.adminOnly && !appStore.isAdmin()) {
        return landing(appStore.isAdmin())
    }

    return true
})

export default router
